import React, { useEffect, useMemo, useState } from 'react';
import { getErrorMessage } from '../services/http';
import { IntegrationsAPI } from '../services/integrations';

const ROLE_OPTIONS = ['admin', 'operacional', 'comercial', 'tecnico', 'financeiro'];

function linesToList(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((value) => value.trim())
    .filter(Boolean);
}

function createAreaRow(area = {}) {
  return {
    id: `${area.code || 'area'}-${Date.now()}-${Math.random()}`,
    code: area.code || '',
    name: area.name || ''
  };
}

function getIntegrationCheckClass(check) {
  if (check?.ok === true) return 'alert-success';
  if (check?.ok === false) return 'alert-danger';
  return 'alert-secondary';
}

function getIntegrationCheckLabel(check) {
  if (check?.ok === true) return 'Funcionando';
  if (check?.ok === false) return 'Falhou';
  return 'Não testado';
}

export default function AdminSettingsPanel({
  workflowSettings,
  users,
  onSaveWorkflow,
  onSaveUser
}) {
  const [statusesText, setStatusesText] = useState('');
  const [stagesText, setStagesText] = useState('');
  const [prioritiesText, setPrioritiesText] = useState('');
  const [blingPaymentMethodId, setBlingPaymentMethodId] = useState('');
  const [areasDraft, setAreasDraft] = useState([]);
  const [savingWorkflow, setSavingWorkflow] = useState(false);
  const [workflowMessage, setWorkflowMessage] = useState('');
  const [workflowError, setWorkflowError] = useState('');
  const [userDrafts, setUserDrafts] = useState({});
  const [savingUserCode, setSavingUserCode] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [userError, setUserError] = useState('');
  const [cecStatus, setCecStatus] = useState(null);
  const [cecLoading, setCecLoading] = useState(false);
  const [cecError, setCecError] = useState('');

  const availableAreas = useMemo(() => workflowSettings?.areas || [], [workflowSettings]);

  useEffect(() => {
    setStatusesText((workflowSettings?.statuses || []).join('\n'));
    setStagesText((workflowSettings?.stages || []).join('\n'));
    setPrioritiesText((workflowSettings?.priorities || []).join('\n'));
    setBlingPaymentMethodId(workflowSettings?.integrations?.bling?.defaultPaymentMethodId || '');
    setAreasDraft((workflowSettings?.areas || []).map((area) => createAreaRow(area)));
  }, [workflowSettings]);

  useEffect(() => {
    const drafts = {};
    for (const user of users || []) {
      drafts[user.code] = {
        role: user.role || 'operacional',
        areas: Array.isArray(user.areas) ? [...user.areas] : []
      };
    }
    setUserDrafts(drafts);
  }, [users]);

  const handleAreaChange = (rowId, field, value) => {
    setAreasDraft((current) => current.map((row) => (
      row.id === rowId ? { ...row, [field]: value } : row
    )));
  };

  const handleUserDraftChange = (code, updates) => {
    setUserDrafts((current) => ({
      ...current,
      [code]: {
        ...current[code],
        ...updates
      }
    }));
  };

  const toggleUserArea = (code, areaCode) => {
    const currentAreas = userDrafts[code]?.areas || [];
    const exists = currentAreas.includes(areaCode);
    handleUserDraftChange(code, {
      areas: exists
        ? currentAreas.filter((item) => item !== areaCode)
        : [...currentAreas, areaCode]
    });
  };

  const saveWorkflow = async () => {
    setSavingWorkflow(true);
    setWorkflowError('');
    setWorkflowMessage('');

    try {
      await onSaveWorkflow({
        statuses: linesToList(statusesText),
        stages: linesToList(stagesText),
        priorities: linesToList(prioritiesText),
        integrations: {
          ...(workflowSettings?.integrations || {}),
          bling: {
            ...(workflowSettings?.integrations?.bling || {}),
            defaultPaymentMethodId: blingPaymentMethodId.trim()
          }
        },
        areas: areasDraft
          .map((area) => ({ code: area.code.trim(), name: area.name.trim() }))
          .filter((area) => area.code && area.name)
      });
      setWorkflowMessage('Configurações atualizadas com sucesso.');
    } catch (error) {
      setWorkflowError(error.message || 'Não foi possível salvar as configurações.');
    } finally {
      setSavingWorkflow(false);
    }
  };

  const saveUser = async (user) => {
    const draft = userDrafts[user.code];
    if (!draft) return;

    setSavingUserCode(user.code);
    setUserError('');
    setUserMessage('');

    try {
      await onSaveUser(user.code, draft);
      setUserMessage(`Perfil de ${user.name} atualizado.`);
    } catch (error) {
      setUserError(error.message || 'Não foi possível salvar o usuário.');
    } finally {
      setSavingUserCode('');
    }
  };

  const testCecIntegration = async () => {
    setCecLoading(true);
    setCecError('');

    try {
      const status = await IntegrationsAPI.cecDiagnostics();
      setCecStatus(status);
    } catch (error) {
      setCecError(getErrorMessage(error, 'Não foi possível testar a integração CEC.'));
    } finally {
      setCecLoading(false);
    }
  };

  return (
    <div className="admin-layout mt-4">
      <section className="panel-section">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <div className="eyebrow">Integrações</div>
            <h2>CEC Integra</h2>
            <p className="text-muted mb-0">Verifique a comunicação entre Task Hub, CEC Integra, Bling e AUVO.</p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-outline-primary" type="button" onClick={testCecIntegration} disabled={cecLoading}>
              {cecLoading ? 'Testando...' : 'Testar comunicação'}
            </button>
            <button className="btn btn-outline-success" type="button" onClick={() => { window.location.href = '/sync-center'; }}>
              Central de Sincronização
            </button>
            <button className="btn btn-primary" type="button" onClick={saveWorkflow} disabled={savingWorkflow}>
              {savingWorkflow ? 'Salvando...' : 'Salvar integrações'}
            </button>
          </div>
        </div>

        {cecError && <div className="alert alert-danger mt-3 mb-0">{cecError}</div>}
        {workflowError && <div className="alert alert-danger mt-3 mb-0">{workflowError}</div>}
        {workflowMessage && <div className="alert alert-success mt-3 mb-0">{workflowMessage}</div>}
        {cecStatus && (
          <div className="mt-3">
            <div className={`alert mb-3 ${cecStatus.ok ? 'alert-success' : 'alert-warning'}`}>
              <strong>{cecStatus.ok ? 'Comunicação básica funcionando.' : 'Há falhas ou pendências na integração.'}</strong>
              <div className="small mt-1">
                URL: {cecStatus.baseUrl || 'não configurada'} · Token: {cecStatus.tokenConfigured ? 'configurado' : 'não configurado'}
                {cecStatus.checkedAt ? ` · Testado em ${new Date(cecStatus.checkedAt).toLocaleString('pt-BR')}` : ''}
              </div>
            </div>

            <div className="admin-grid">
              {(cecStatus.checks || []).map((check) => (
                <div className={`alert mb-0 ${getIntegrationCheckClass(check)}`} key={check.key}>
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <strong>{check.label}</strong>
                    <span className="badge bg-light text-dark">{getIntegrationCheckLabel(check)}</span>
                  </div>
                  <div className="small mt-2">
                    Direção: {check.direction || 'n/a'}
                    {check.operation ? ` · ${check.operation}` : ''}
                    {Number.isFinite(Number(check.durationMs)) ? ` · ${check.durationMs}ms` : ''}
                  </div>
                  {check.reason && <div className="small mt-2">{check.reason}</div>}
                  {check.message && <div className="small mt-2">{check.message}</div>}
                  {check.sample?.total !== undefined && <div className="small mt-2">Total retornado: {check.sample.total}</div>}
                  {check.sample?.count !== undefined && <div className="small mt-2">Itens na amostra: {check.sample.count}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="row g-3 mt-4">
          <div className="col-md-6">
            <label className="form-label">Forma de pagamento padrão do Bling</label>
            <input
              className="form-control"
              value={blingPaymentMethodId}
              onChange={(event) => setBlingPaymentMethodId(event.target.value)}
              placeholder="ID da forma de pagamento"
            />
            <div className="small text-muted mt-2">Usada nas parcelas das propostas criadas pelos centros de custo.</div>
          </div>
        </div>
      </section>

      <section className="panel-section">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <div className="eyebrow">Administração</div>
            <h2>Fluxo operacional</h2>
            <p className="text-muted mb-0">Cadastre os status, etapas, prioridades e áreas que vão aparecer no painel e na edição das tarefas.</p>
          </div>
          <button className="btn btn-primary" type="button" onClick={saveWorkflow} disabled={savingWorkflow}>
            {savingWorkflow ? 'Salvando...' : 'Salvar configurações'}
          </button>
        </div>

        {workflowError && <div className="alert alert-danger mt-3 mb-0">{workflowError}</div>}
        {workflowMessage && <div className="alert alert-success mt-3 mb-0">{workflowMessage}</div>}

        <div className="admin-grid mt-4">
          <div>
            <label className="form-label">Status</label>
            <textarea
              className="form-control"
              rows={8}
              value={statusesText}
              onChange={(event) => setStatusesText(event.target.value)}
              placeholder="Um status por linha"
            />
          </div>

          <div>
            <label className="form-label">Etapas</label>
            <textarea
              className="form-control"
              rows={8}
              value={stagesText}
              onChange={(event) => setStagesText(event.target.value)}
              placeholder="Uma etapa por linha"
            />
          </div>

          <div>
            <label className="form-label">Prioridades</label>
            <textarea
              className="form-control"
              rows={8}
              value={prioritiesText}
              onChange={(event) => setPrioritiesText(event.target.value)}
              placeholder="Uma prioridade por linha"
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
            <div>
              <label className="form-label mb-1">Áreas</label>
              <div className="small text-muted">Defina o código interno e o nome que será exibido no painel.</div>
            </div>
            <button
              className="btn btn-outline-secondary btn-sm"
              type="button"
              onClick={() => setAreasDraft((current) => [...current, createAreaRow()])}
            >
              Nova área
            </button>
          </div>

          <div className="admin-area-list mt-3">
            {areasDraft.map((area) => (
              <div key={area.id} className="admin-area-row">
                <input
                  className="form-control"
                  value={area.code}
                  onChange={(event) => handleAreaChange(area.id, 'code', event.target.value)}
                  placeholder="codigo"
                />
                <input
                  className="form-control"
                  value={area.name}
                  onChange={(event) => handleAreaChange(area.id, 'name', event.target.value)}
                  placeholder="Nome da área"
                />
                <button
                  className="btn btn-outline-danger"
                  type="button"
                  onClick={() => setAreasDraft((current) => current.filter((row) => row.id !== area.id))}
                >
                  Remover
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="panel-section">
        <div>
          <div className="eyebrow">Administração</div>
          <h2>Usuários e áreas</h2>
          <p className="text-muted mb-0">Vincule cada usuário às áreas de atuação e ajuste o perfil quando necessário.</p>
        </div>

        {userError && <div className="alert alert-danger mt-3 mb-0">{userError}</div>}
        {userMessage && <div className="alert alert-success mt-3 mb-0">{userMessage}</div>}

        <div className="user-admin-list mt-4">
          {(users || []).map((user) => {
            const draft = userDrafts[user.code] || { role: user.role || 'operacional', areas: user.areas || [] };
            const isSaving = savingUserCode === user.code;

            return (
              <div key={user.code} className="user-admin-card">
                <div className="d-flex justify-content-between gap-3 flex-wrap">
                  <div>
                    <strong>{user.name}</strong>
                    <div className="small text-muted">{user.email || user.code}</div>
                  </div>
                  <button className="btn btn-outline-primary btn-sm" type="button" onClick={() => saveUser(user)} disabled={isSaving}>
                    {isSaving ? 'Salvando...' : 'Salvar usuário'}
                  </button>
                </div>

                <div className="row g-3 mt-1">
                  <div className="col-md-4">
                    <label className="form-label">Perfil</label>
                    <select
                      className="form-select"
                      value={draft.role}
                      onChange={(event) => handleUserDraftChange(user.code, { role: event.target.value })}
                    >
                      {ROLE_OPTIONS.map((role) => <option key={role} value={role}>{role}</option>)}
                    </select>
                  </div>
                  <div className="col-md-8">
                    <label className="form-label">Áreas vinculadas</label>
                    <div className="area-checkbox-grid">
                      {availableAreas.map((area) => (
                        <label key={area.code} className="area-checkbox">
                          <input
                            type="checkbox"
                            checked={draft.areas.includes(area.code)}
                            onChange={() => toggleUserArea(user.code, area.code)}
                          />
                          <span>{area.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
